import { ApiGet, ApiPost, ApiPostStream, ApiPostWithLargeIntegerSupport } from './Common.ts'
import { GetWebConnectorUrl, GetOrderEntryConnectorUrl, GetWebConnectorExtensionsUrl } from '../stores/SettingsStore.ts'
import { PwsHstBatchCurrentGet } from './Business.ts'
import type { ComposerTranslation } from 'vue-i18n'
import { handleError } from '../services/ErrorService'
import type {
  IDocumentsResponse,
  IBusinessDetailsResponse,
  IPwsDocumentSaveRequest,
  IPwsDocumentSaveResponse,
  ISocialMediaRequest,
  ISocialMediaResponse,
  IPhotoUploadRequest,
  IPhotoResponse,
  IContractsResponse,
  IPwsActiveBeeIncentiveGetListResponse,
  IPwsIncentTransDetailsGetListResponse,
  IBusinessEntityPerformanceMetricGetListResponse,
  IWalletCreateTokenResponse,
  INewUserWizardCompletedRequest,
  IBeeSecurityResponseGetResponse,
  IPwsBeeTaxPayerGovermentIdGetRequest,
  IPwsBeeTaxPayerGovermentIdGetResponse,
  ITpcRenewalStatusGetResponse,
  IRenewalChangePlanRequest,
  IRenewalChangePlanResponse,
  IRenewalProgramsGetResponse,
  IRenewalRevertPendingChangesResponse,
  IRenewalEnrollRequest,
  IRenewalEnrollResponse,
  IRenewalCancelResponse
} from '../types/ConsultantCornerTypes'
import type {
  IBeeRenewalChangePlanRequest,
  IBeeRenewalChangePlanResponse,
  IBeeRenewalUnenrollResponse,
  IBeeRenewalUpdatePendingChangesRequest,
  IBeeRenewalUpdatePendingChangesResponse,
  IBeeRenewalRevertPendingChangesResponse
} from '../types/OrderTypes'
import type { ITeamStructureResponse } from '../types/TeamStructureTypes'

export async function GetBusinessEntityDocuments(
  beeNumber: number,
): Promise<IDocumentsResponse> {
  const url = await GetWebConnectorUrl('PwsDocumentGetList')
  return ApiGet(url)
}

export async function GetDocumentFileUrl(
  beeNumber: number,
  beeDocumentGuid: number,
): Promise<string> {
  const baseUrl = await GetWebConnectorUrl('PwsDocumentFileGet')
  const params = new URLSearchParams({
    beeNumber: beeNumber.toString(),
    beeDocumentGuid: beeDocumentGuid.toString(),
    expires: '3600'
  })
  return `${baseUrl}?${params.toString()}`
}

export async function GetBusinessDetails(
  beeNumber: number,
  t: ComposerTranslation,
): Promise<IBusinessDetailsResponse | null> {
  try {
    const batchResponse = await PwsHstBatchCurrentGet()
    const batchNumber = batchResponse?.currentBatchNumber

    if (!batchNumber) {
      await handleError(new Error(t('alerts.FailedToGetCurrentBatchNumber')), t)
      return null
    }

    // Define the fields we need to request
    const fieldNames = [
      'BeeNumber',
      'FirstName',
      'LastName',
      'Email',
      'Phone',
      'Mobile',
      'Username',
      'HomeCountry',
      'PersonalStory',
      'ConsultantWebsite',
      'CompanyURL',
      'VatId',
      'TaxId',
      'ProfilePhotoUrl',
      'DeliveryCountry',
      'DeliveryStreet',
      'DeliveryHouseNumber',
      'DeliveryPostalCode',
      'DeliveryCity',
      'Facebook',
      'Instagram',
      'Pinterest',
      'Youtube',
      'Tiktok'
    ]

    // Now call PwsBusinessDetailsGet with the batch number and field names
    const url = await GetWebConnectorUrl('PwsBusinessDetailsGet')
    const request = {
      beeNumber: beeNumber,
      batchNumber: batchNumber,
      fieldName: fieldNames
    }
    return ApiPost(url, request)
  } catch (error) {
    console.error('Error in GetBusinessDetails:', error)
    await handleError(error, t)
    return null
  }
}

export async function PwsProfileSocialGet(request: {
  beeNumber: number
}): Promise<ISocialMediaResponse> {
  const url = await GetWebConnectorUrl('PwsProfileSocialGet')
  return ApiPost(url, request)
}

export async function PwsProfileSocialSave(
  request: ISocialMediaRequest,
): Promise<any> {
  const url = await GetWebConnectorUrl('PwsProfileSocialSave')
  return ApiPost(url, request)
}

export interface PwsProfilePhotoGetRequest {
  beeNumber?: number
}

export interface RejectedPhoto {
  beePhotoGuid: number
  uri: string
  reason: string
}

export interface PwsProfilePhotoGetResponse {
  beePhotoGuid?: number
  approvedURI?: string
  rejectedPhoto?: RejectedPhoto[]
}

export interface PwsProfilePhotoUploadRequest {
  fileName: string
  file: File
  skipValidation?: boolean
}

export interface PwsProfilePhotoUploadResponse {
  beePhotoGuid?: number
}

export async function PwsProfilePhotoGet(request: PwsProfilePhotoGetRequest): Promise<PwsProfilePhotoGetResponse> {
  const url = await GetWebConnectorUrl('PwsProfilePhotoGet')
  return ApiPost(url, request)
}

export async function PwsProfilePhotoUpload(
  request: PwsProfilePhotoUploadRequest,
): Promise<PwsProfilePhotoUploadResponse> {
  const url = await GetWebConnectorUrl('PwsProfilePhotoUpload')
  console.log('request', request)

  // For IRequiresRequestStream services, send file as raw body with FileName as query parameter
  const queryParams: Record<string, string> = {
    FileName: request.fileName
  }

  // Add SkipValidation if provided
  if (request.skipValidation !== undefined) {
    queryParams.SkipValidation = request.skipValidation.toString()
  }

  return ApiPostStream(url, request.file, queryParams)
}

export async function PwsProfileTermsGetList(request?: { beeNumber: number }): Promise<IContractsResponse> {
  const url = await GetWebConnectorUrl('PwsProfileTermsGetList')
  return ApiGet(url, request)
}

export async function PwsSecurityQuestionsGet(request: {
  beeNumber: number
}): Promise<any> {
  throw new Error(
    'Security Questions API is not yet implemented. This is a placeholder for future development.',
  )
}

export interface PwsSecurityResponseSaveRequest {
  GenericQuestionNumber: number
  GenericQuestionResponse: string
  CustomQuestion: string
  CustomQuestionResponse: string
}

export async function PwsSecurityResponseSave(
  request: PwsSecurityResponseSaveRequest,
): Promise<any> {
  const url = await GetOrderEntryConnectorUrl('PwsSecurityResponseSaveConnector')
  return ApiPost(url, request)
}

/**
 * Gets the saved security questions for the authenticated user
 * @param returnAnswers - When true, includes the answer text in the response (default: false for security)
 * @returns Promise with the list of saved security responses
 */
export async function BeeSecurityResponseGetConnector(returnAnswers: boolean = false): Promise<IBeeSecurityResponseGetResponse> {
  const url = await GetOrderEntryConnectorUrl('BeeSecurityResponseGetConnector')
  if (returnAnswers) {
    return ApiGet(url, { ReturnAnswers: true })
  }
  return ApiGet(url)
}

export async function PwsPerformanceMetricsGet(request?: {
  genPlanGuid?: number
}): Promise<IBusinessEntityPerformanceMetricGetListResponse> {
  const url = await GetWebConnectorUrl('PwsPerformanceMetricsGet')
  return ApiGet(url, request)
}

export async function PwsPersonalMetricsGet(request: {
  fieldName: string[]
}): Promise<any> {
  const url = await GetWebConnectorUrl('PwsPersonalMetricsGet')
  return ApiPost(url, request)
}

export async function PwsAppPeriodDateGetList(): Promise<any> {
  const url = await GetWebConnectorUrl('PwsAppPeriodDateGetList')
  return ApiGet(url)
}

export async function PwsTeamStructureGet(request: {
  maxLevelsDisplayed?: number
  batchNumber: number
}): Promise<ITeamStructureResponse> {
  const url = await GetWebConnectorUrl('PwsTeamStructureGet')
  return ApiPost(url, request)
}

export async function PwsTeamDetailsGet(request: {
  batchNumber: number
  maxLevelsDisplayed: number
  reportName: string
  fieldList: string[]
}): Promise<any> {
  const url = await GetWebConnectorUrl('PwsTeamDetailsGet')
  return ApiPost(url, request)
}

export interface DynamicReportColumn {
  columnName: string
  localizedColumnName: string
  displayOrder: number
  mobileDisplayOrder?: number
  mobileDetailDisplayOrder?: number
  dataType: 'DecimalDataType' | 'StringDataType' | 'DateDataType'
  isFilter: boolean
  isFilterOnly: boolean
  minWidth?: number
}

export interface DynamicReportDefinition {
  columns: DynamicReportColumn[]
  filters: any[]
  isPerformanceMetric: boolean
  codeIdDynamicReportCategory: {
    codeId: string
    name: string
    localizedValue: string
    categoryName: string
    categoryId: string
  }
  reportId: string
  localizedReportName: string
  localizedReportDescription: string
  displayOrder: number
}

export async function PwsDynamicReportGet(
  reportId: string,
  beeNumber?: number,
): Promise<{ item: DynamicReportDefinition }> {
  const url = await GetWebConnectorUrl('PwsDynamicReportGet')
  return ApiPost(url, { reportId, beeNumber })
}

export async function PwsDynamicReportGetList(categoryCodeId?: string): Promise<any> {
  const url = await GetWebConnectorUrl('PwsDynamicReportGetList')

  // Note: The API returns categories, and filtering needs to happen client-side
  // The ResponseFilter applies to the report level, not the category level
  // So we'll just get all reports and filter in the component
  return ApiPost(url, {})
}

export async function PwsDynamicReportExecute(request: {
  reportId: string
  ColumnNames: string[]
  Filters: any[]
}): Promise<any> {
  const url = await GetWebConnectorUrl('PwsDynamicReportExecute')
  return ApiPost(url, request)
}

export async function PwsPerformanceMetricsSummaryGet(): Promise<any> {
  const url = await GetWebConnectorUrl('PwsPerformanceMetricsSummaryGet')
  return ApiPost(url, {})
}

export async function PwsBusinessDetailsGet(request: {
  batchNumber: number
  fieldName: string[]
}): Promise<any> {
  const url = await GetWebConnectorUrl('PwsBusinessDetailsGet')
  return ApiPost(url, request)
}

export async function PwsDocumentSave(
  request: IPwsDocumentSaveRequest,
): Promise<IPwsDocumentSaveResponse> {
  const url = await GetWebConnectorUrl('PwsDocumentSave')
  return ApiPost(url, request)
}

export async function PwsDocumentSaveWithLargeIntegerSupport(
  request: IPwsDocumentSaveRequest,
): Promise<IPwsDocumentSaveResponse> {
  const url = await GetWebConnectorUrl('PwsDocumentSave')
  return ApiPostWithLargeIntegerSupport(url, request)
}

export async function PwsDocumentFileSave(
  beeDocumentGuid: string,
  file: File,
): Promise<void> {
  const url = await GetWebConnectorUrl('PwsDocumentFileSave')
  console.log(beeDocumentGuid)
  const queryParams = {
    beeDocumentGuid: beeDocumentGuid
  }
  console.log(queryParams)

  return ApiPostStream(url, file, queryParams)
}

export async function PwsActiveBeeIncentiveGetList(request?: {
  genPlanGuid?: number
}): Promise<IPwsActiveBeeIncentiveGetListResponse> {
  const url = await GetWebConnectorUrl('PwsActiveBeeIncentiveGetList')
  return ApiGet(url, request)
}

export async function PwsIncentTransDetailsGetList(request: {
  beeNumber: number
  programId?: string
  awardCriteria?: number
  month?: number
  year?: number
}): Promise<IPwsIncentTransDetailsGetListResponse> {
  const url = await GetWebConnectorUrl('PwsIncentTransDetailsGetList')
  return ApiPost(url, request)
}

export async function WalletTokenCreate(request: {
  controlFolderName: string
  countryCode: string
}): Promise<IWalletCreateTokenResponse> {
  const url = await GetOrderEntryConnectorUrl('WalletTokenCreate')
  return ApiPost(url, request)
}

/**
 * Gets the business name for a given BEE number
 * @returns Promise with the business name response
 */
export async function PwsProfileBusinessNameGet(): Promise<{ BusinessName: string}> {
  const url = await GetWebConnectorUrl('PwsProfileBusinessNameGet')
  return ApiGet(url)
}

/**
 * Registers the completion of the New User Wizard process
 * @param request Object containing the beeNumber
 * @returns Promise with the response
 */
export async function NewUserWizardCompleted(request: INewUserWizardCompletedRequest): Promise<any> {
  const url = await GetOrderEntryConnectorUrl('NewUserWizardCompleted')
  return ApiPost(url, request)
}

export interface PwsProfileGeneralViewModel {
  beeNumber?: number
  firstName: string
  lastName: string
  preferredName: string
  alternateFirstName: string
  alternateLastName: string
  alternatePreferredName: string
  timeZone: string
  emailAddress: string
  primaryPhone: PhoneModel
  altPhone1: PhoneModel
  altPhone2: PhoneModel
  altPhone3: PhoneModel
}

export interface PhoneModel {
  countryCode: string
  phoneNumber: string // This is for form binding only
  phoneType: string
  // Additional fields from API response
  codeIdPhoneType?: string
  number?: string
  formattedNumber?: string
}

export async function PwsProfileGeneralGet(): Promise<{item: PwsProfileGeneralViewModel}> {
  const url = await GetWebConnectorUrl('PwsProfileGeneralGet')
  return ApiGet(url)
}

export async function PwsProfileGeneralSave(
  request: PwsProfileGeneralViewModel,
): Promise<any> {
  const url = await GetWebConnectorUrl('PwsProfileGeneralSave')
  return ApiPost(url, request)
}

export interface AddressModel {
  addressLine1: string
  addressLine2?: string
  city: string
  stateCode: string
  postalCode: string
  countryCode: string
  firstName?: string
  lastName?: string
  houseNumber?: string
}

export interface ProfileAddressModel {
  addressId: number
  address: AddressModel
}

export interface PwsProfileAddressViewModel {
  beeNumber?: number
  shippingAddress?: ProfileAddressModel
  billingAddress?: ProfileAddressModel
  mailingAddress?: ProfileAddressModel
  allAddresses?: ProfileAddressModel[]
}

export interface PwsProfileAddressSaveRequest {
  AddressId?: number
  Address: AddressModel
  IsDefaultBilling: boolean
  IsDefaultShipping: boolean
  IsDefaultMailing: boolean
}

export async function PwsProfileAddressGet(): Promise<{item: PwsProfileAddressViewModel}> {
  const url = await GetWebConnectorUrl('PwsProfileAddressGet')
  return ApiGet(url)
}

export async function PwsProfileAddressSave(
  request: PwsProfileAddressSaveRequest,
): Promise<any> {
  const url = await GetWebConnectorUrl('PwsProfileAddressSave')
  return ApiPost(url, request)
}

export interface PwsProfileStoryViewModel {
  beeNumber?: number
  storyText: string
  isStoryApproved?: boolean
}

export async function PwsProfileStoryGet(): Promise<{item: PwsProfileStoryViewModel}> {
  const url = await GetWebConnectorUrl('PwsProfileStoryGet')
  return ApiGet(url)
}

export async function PwsProfileStorySave(
  request: PwsProfileStoryViewModel,
): Promise<any> {
  const url = await GetWebConnectorUrl('PwsProfileStorySave')

  // Create request with IsStoryApproved in PascalCase for API
  const requestData: any = {
    beeNumber: request.beeNumber,
    storyText: request.storyText
  }

  if (request.isStoryApproved !== undefined) {
    requestData.IsStoryApproved = request.isStoryApproved
  }

  return ApiPost(url, requestData)
}

export interface RenewalSettingsViewModel {
  beeNumber?: number
  planName: string
  codeIdProcessStatus: string
  renewalDate?: Date
  renewalFrequency: string
  renewalId?: number
  arePendingChanges?: boolean
  renewalProgramId: string
  currentOptionalPrograms: string[]
  futureOptionalPrograms: string[]
  futurePeriodName: string
  futureProgramName: string
  isSecondaryPlan: boolean
  secondaryCodeIdProcessStatus: string
  secondaryRenewalDate?: Date
  secondaryPlanName: string
  nextChargeAmount: number
}

export async function RenewalSettingsGet(): Promise<{item: RenewalSettingsViewModel}> {
  const url = await GetOrderEntryConnectorUrl('RenewalSettingsGet')

  return ApiGet(url)
}

export interface PwsBeeConsultantSearchOptoutViewModel {
  optOut: boolean
}

export async function PwsBeeConsultantSearchOptoutGet(): Promise<{item: PwsBeeConsultantSearchOptoutViewModel}> {
  const url = await GetWebConnectorUrl('PwsBeeConsultantSearchOptoutGet')
  return ApiGet(url)
}

export async function PwsBeeConsultantSearchOptoutSave(
  request: PwsBeeConsultantSearchOptoutViewModel,
): Promise<any> {
  const url = await GetWebConnectorUrl('PwsBeeConsultantSearchOptoutSave')
  return ApiPost(url, request)
}

export async function PwsBeeConsultantSearchOptoutUpdate(
  request: PwsBeeConsultantSearchOptoutViewModel,
): Promise<any> {
  const url = await GetWebConnectorUrl('PwsBeeConsultantSearchOptoutUpdate')
  return ApiPost(url, request)
}

/**
 * Gets wallet information with proper GUID handling to avoid JavaScript rounding issues
 * Uses PwsDocumentGetList with large integer support to retrieve correct wallet GUIDs
 * @returns Promise with wallet information containing proper GUID strings
 */
export async function PwsWalletGetListWithGuidSupport(): Promise<any> {
  const url = await GetWebConnectorUrl('PwsDocumentGetList')
  return ApiPostWithLargeIntegerSupport(url, {})
}

/**
 * Gets the government ID (SIRET) for a taxpayer
 * @param request Object containing govermentIdType and countryCode
 * @returns Promise with the government ID response
 */
export async function PwsBeeTaxPayerGovermentIdGet(
  request: IPwsBeeTaxPayerGovermentIdGetRequest,
): Promise<IPwsBeeTaxPayerGovermentIdGetResponse> {
  const url = await GetOrderEntryConnectorUrl('PwsBeeTaxPayerGovermentIdGet')
  return ApiPost(url, request)
}

/**
 * Changes the renewal plan with new frequency and kit selection
 * @param request Object containing renewalProgramId, newRenewalFrequency, newGenKitId, and previousGenKitId
 * @returns Promise with the response
 */
export async function BeeRenewalChangePlanConnector(
  request: IBeeRenewalChangePlanRequest,
): Promise<IBeeRenewalChangePlanResponse> {
  const url = await GetOrderEntryConnectorUrl('BeeRenewalChangePlanConnector')
  return ApiPost(url, request)
}

/**
 * Unenrolls from the renewal program
 * @returns Promise with the updated renewal settings
 */
export async function BeeRenewalUnenrollConnector(): Promise<IBeeRenewalUnenrollResponse> {
  const url = await GetOrderEntryConnectorUrl('BeeRenewalUnenrollConnector')
  return ApiGet(url)
}

/**
 * Updates pending changes for the renewal plan
 * @param request Object containing renewalId, renewalProgramId, renewalFrequency, and newKitId
 * @returns Promise with the response
 */
export async function BeeRenewalUpdatePendingChangesConnector(
  request: IBeeRenewalUpdatePendingChangesRequest,
): Promise<IBeeRenewalUpdatePendingChangesResponse> {
  const url = await GetOrderEntryConnectorUrl('BeeRenewalUpdatePendingChangesConnector')
  return ApiPost(url, request)
}

/**
 * Reverts pending changes for the renewal plan
 * @returns Promise with the response
 */
export async function BeeRenewalRevertPendingChangesConnector(): Promise<IBeeRenewalRevertPendingChangesResponse> {
  const url = await GetOrderEntryConnectorUrl('BeeRenewalRevertPendingChangesConnector')
  return ApiGet(url)
}

/**
 * Re-enrolls a consultant in the renewal program
 * @returns Promise with the updated renewal settings
 */
export async function BeeRenewalReenrollConnector(): Promise<IBeeRenewalUnenrollResponse> {
  const url = await GetOrderEntryConnectorUrl('BeeRenewalReenrollConnector')
  return ApiGet(url)
}

export interface PwsProfileBeeUplineLevelGetResponse {
  item: {
    sponsorFirstName: string
    sponsorLastName: string
    recruiterFirstName: string
    recruiterLastName: string
    personalGroupFirstName?: string
    personalGroupLastName?: string
  }
}

/**
 * Gets the upline information (sponsor and recruiter) for a given BeeNumber
 * @param beeNumber The BeeNumber to get upline information for
 * @returns Promise with sponsor and recruiter information
 */
export async function PwsProfileBeeUplineLevelGet(beeNumber: number): Promise<PwsProfileBeeUplineLevelGetResponse> {
  const url = await GetWebConnectorUrl('PwsProfileBeeUplineLevelGet')
  return ApiPost(url, { beeNumber })
}

/**
 * Gets the renewal status for the current user
 * @returns Promise with renewal status information including status, program ID, renewal date, and frequency
 */
export async function TpcRenewalStatusGet(): Promise<ITpcRenewalStatusGetResponse> {
  const url = await GetWebConnectorExtensionsUrl('RenewalStatusGet')
  return ApiGet(url)
}

/**
 * Changes the renewal plan (e.g., start trial, change frequency)
 * @param request Request with newProgramId and optional frequency
 * @returns Promise with response status
 */
export async function RenewalChangePlan(request: IRenewalChangePlanRequest): Promise<IRenewalChangePlanResponse> {
  const url = await GetWebConnectorExtensionsUrl('RenewalChangePlanRequest')
  return ApiPost(url, request)
}

/**
 * Gets available renewal programs and their frequencies with prices
 * @returns Promise with program information including frequencies and prices
 */
export async function RenewalProgramsGet(): Promise<IRenewalProgramsGetResponse> {
  const url = await GetWebConnectorExtensionsUrl('RenewalProgramsGet')
  return ApiGet(url)
}

/**
 * Reverts pending plan changes
 * @returns Promise with response status
 */
export async function TpcRenewalRevertPendingChanges(): Promise<IRenewalRevertPendingChangesResponse> {
  const url = await GetWebConnectorExtensionsUrl('RenewalRevertPendingChangesRequest')
  return ApiPost(url, {})
}

/**
 * Enrolls user in renewal program with selected frequency
 * @param request Request with frequency
 * @returns Promise with response status
 */
export async function RenewalEnroll(request: IRenewalEnrollRequest): Promise<IRenewalEnrollResponse> {
  const url = await GetWebConnectorExtensionsUrl('RenewalEnrollRequest')
  return ApiPost(url, request)
}

/**
 * Cancels the renewal subscription
 * @returns Promise with response status
 */
export async function RenewalCancel(): Promise<IRenewalCancelResponse> {
  const url = await GetWebConnectorExtensionsUrl('RenewalCancelRequest')
  return ApiPost(url, {})
}
